/**
 * Browser-side event queue with periodic flush + unload drain.
 *
 * Instead of firing one `sendBeacon` per event (which can silently drop
 * under browser pending-data limits during rapid step transitions), this
 * queue batches events and flushes them in a single payload.
 *
 * Flush triggers:
 *   1. Timer — every `flushIntervalMs` (default 500 ms).
 *   2. Capacity — when the queue reaches `maxBatchSize` (default 20).
 *   3. Unload — `visibilitychange` (hidden) + `pagehide` drain via
 *      sendBeacon so events survive navigation.
 *
 * NX-10156
 */

export type EventQueueTransport = {
  /** Synchronous beacon — returns `true` when queued. */
  readonly sendBeacon: (url: string, body: string) => boolean;
  /** Async fallback (POST with keepalive). */
  readonly fetch: (url: string, body: string) => Promise<void>;
};

export type EventQueueOptions = {
  /** POST target. Defaults to `/api/events/batch`. */
  readonly endpoint?: string;
  /** Milliseconds between automatic flushes. Defaults to 500. */
  readonly flushIntervalMs?: number;
  /** Max events buffered before an eager flush. Defaults to 20. */
  readonly maxBatchSize?: number;
  /** Transport override for tests. */
  readonly transport?: EventQueueTransport;
};

const DEFAULT_ENDPOINT = "/api/events/batch";
const DEFAULT_FLUSH_INTERVAL_MS = 500;
/**
 * Client-side cap — keep batches small so `sendBeacon` payloads stay
 * well under the browser ~64 KB limit even on unload.  The server-side
 * MAX_BATCH_SIZE (50) is intentionally higher to tolerate batches that
 * merge from retries or multiple flush cycles.
 */
const DEFAULT_MAX_BATCH_SIZE = 20;
/**
 * How many times a single event may be re-sent after a failed flush.
 *
 * Delivery is at-least-once: a flush that fails locally may still have been
 * accepted by the server (a lost response, or an aborted keepalive request on
 * unload), and the retry then stores the event twice. Consumers deduplicate on
 * `event_id`, but an event that keeps failing would otherwise be retried for
 * the lifetime of the page — production shows single events archived four
 * times. Capping the attempts bounds that amplification.
 */
const MAX_SEND_ATTEMPTS = 3;

const beaconFromGlobal = (url: string, body: string): boolean => {
  const nav =
    typeof navigator !== "undefined"
      ? (navigator as Navigator & {
          sendBeacon?: (url: string, body: BodyInit) => boolean;
        })
      : null;
  if (nav?.sendBeacon === undefined) return false;
  const blob = new Blob([body], { type: "application/json" });
  try {
    return nav.sendBeacon(url, blob);
  } catch {
    return false;
  }
};

const fetchFromGlobal = async (url: string, body: string): Promise<void> => {
  if (typeof fetch === "undefined") return;
  const res = await fetch(url, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  });
  if (!res.ok) {
    throw new Error(`Event batch POST failed: ${res.status}`);
  }
};

const DEFAULT_TRANSPORT: EventQueueTransport = {
  sendBeacon: beaconFromGlobal,
  fetch: fetchFromGlobal,
};

export type EventQueue = {
  /** Enqueue a pre-serialised event envelope (JSON string). */
  readonly enqueue: (json: string) => void;
  /** Force-flush everything now (async, uses fetch). */
  readonly flush: () => Promise<void>;
  /**
   * Synchronous best-effort flush via `sendBeacon`.
   * Use before same-origin navigations (e.g. `window.location.assign`) where
   * an async flush cannot complete before the page unloads.
   */
  readonly flushSync: () => void;
  /** Tear down timers and listeners. */
  readonly dispose: () => void;
  /** Number of events currently buffered. */
  readonly pending: () => number;
};

export const createEventQueue = (options: EventQueueOptions = {}): EventQueue => {
  const endpoint = options.endpoint ?? DEFAULT_ENDPOINT;
  const flushIntervalMs = options.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  const maxBatchSize = options.maxBatchSize ?? DEFAULT_MAX_BATCH_SIZE;
  const transport = options.transport ?? DEFAULT_TRANSPORT;

  const MAX_BUFFER = maxBatchSize * 5;
  let buffer: string[] = [];
  let timer: ReturnType<typeof setInterval> | null = null;
  /** Failed-send count per buffered event, keyed by its serialised form. */
  const attempts = new Map<string, number>();
  /** Events removed from the buffer for a send that has not settled yet. */
  let inFlight: string[] = [];

  /**
   * Drop counts for events no longer buffered, so a page that keeps failing
   * does not retain the ones its buffer overflowed for its whole lifetime.
   */
  const forgetUnbuffered = (): void => {
    const known = new Set(buffer.concat(inFlight));
    for (const json of attempts.keys()) {
      if (!known.has(json)) attempts.delete(json);
    }
  };

  /** Re-buffer `batch` without spending its retry budget. */
  const rebuffer = (batch: string[]): void => {
    buffer = batch.concat(buffer).slice(0, MAX_BUFFER);
    forgetUnbuffered();
  };

  /** Re-buffer `batch` for another try, dropping events past the attempt cap. */
  const requeue = (batch: string[]): void => {
    const retryable = batch.filter((json) => {
      const next = (attempts.get(json) ?? 0) + 1;
      if (next >= MAX_SEND_ATTEMPTS) {
        attempts.delete(json);
        return false;
      }
      attempts.set(json, next);
      return true;
    });
    buffer = retryable.concat(buffer).slice(0, MAX_BUFFER);
    forgetUnbuffered();
  };

  /** `list` minus one occurrence of each entry of `removed`. */
  const without = (list: string[], removed: string[]): string[] => {
    const rest = [...list];
    for (const json of removed) {
      const at = rest.indexOf(json);
      if (at !== -1) rest.splice(at, 1);
    }
    return rest;
  };

  /** Forget attempt counts for events that left the buffer for good. */
  const settle = (batch: string[]): void => {
    for (const json of batch) attempts.delete(json);
  };

  const drainViaBeacon = (): void => {
    while (buffer.length > 0) {
      const batch = buffer.slice(0, maxBatchSize);
      buffer = buffer.slice(maxBatchSize);
      const body = `[${batch.join(",")}]`;
      // A refused beacon was never queued by the browser, so it cannot have
      // reached the server and cannot be the source of a duplicate.
      if (!transport.sendBeacon(endpoint, body)) {
        rebuffer(batch);
        break;
      }
      settle(batch);
    }
  };

  /** Sends one batch; false when it failed and was re-buffered. */
  const drainViaFetch = async (): Promise<boolean> => {
    if (buffer.length === 0) return true;
    const batch = buffer.slice(0, maxBatchSize);
    buffer = buffer.slice(maxBatchSize);
    inFlight = inFlight.concat(batch);
    const body = `[${batch.join(",")}]`;
    try {
      await transport.fetch(endpoint, body);
      inFlight = without(inFlight, batch);
      settle(batch);
      return true;
    } catch {
      inFlight = without(inFlight, batch);
      requeue(batch);
      return false;
    }
  };

  const onVisibilityChange = (): void => {
    if (typeof document !== "undefined" && document.visibilityState === "hidden") {
      drainViaBeacon();
    }
  };

  const onPageHide = (): void => {
    drainViaBeacon();
  };

  // Start periodic flush.
  if (typeof setInterval !== "undefined") {
    timer = setInterval(() => {
      void drainViaFetch();
    }, flushIntervalMs);
  }

  // Unload listeners.
  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibilityChange);
  }
  if (typeof addEventListener !== "undefined") {
    addEventListener("pagehide", onPageHide);
  }

  return {
    enqueue(json: string): void {
      if (buffer.length >= MAX_BUFFER) return;
      buffer.push(json);
      if (buffer.length >= maxBatchSize) {
        void drainViaFetch();
      }
    },

    async flush(): Promise<void> {
      // Stop on the first failure rather than on a non-shrinking buffer:
      // dropping a capped event also shrinks it, which would let one flush
      // spend the whole retry budget of the events behind it.
      while (buffer.length > 0) {
        if (!(await drainViaFetch())) break;
      }
    },

    flushSync(): void {
      drainViaBeacon();
    },

    dispose(): void {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }
      if (typeof removeEventListener !== "undefined") {
        removeEventListener("pagehide", onPageHide);
      }
      // Final drain via beacon (best-effort).
      drainViaBeacon();
    },

    pending(): number {
      return buffer.length;
    },
  };
};
